import {
  ExclamationTriangleIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { AssistantStep } from 'services/apis/ai'

/**
 * What the assistant did, shown the way Claude's tool cards read it.
 *
 * Shared by both assistant surfaces - the floating panel and the project workspace - because "show
 * me the steps" means the same thing in each, and the workspace is where a project run is long
 * enough that watching it matters.
 */

/** Steps persisted before the cards existed are bare phrases, so every renderer normalizes first. */
export const normalizeStep = (step: string | AssistantStep): AssistantStep =>
  typeof step === 'string' ? { label: step, kind: 'tool' } : step

/**
 * Fold a step into the list in flight, the way the server folds it into the copy it persists - so
 * the panel while a turn runs and the transcript after it reads the same.
 *
 * A step arrives twice: once when the work starts, once carrying what came back. The second is the
 * first, finished, so it replaces it. Reasoning is the same story told in two phrases - 'Thinking…'
 * going out, 'Thought for 12s' coming back - so it merges on kind rather than on the label.
 *
 * Mutates `steps`, which every caller owns privately and copies into state.
 */
export const mergeStep = (steps: AssistantStep[], step: AssistantStep): AssistantStep[] => {
  const last = steps[steps.length - 1]
  const sameThinking = last?.kind === 'thinking' && step.kind === 'thinking'
  if (last && (sameThinking || (last.label === step.label && last.tool === step.tool))) {
    steps[steps.length - 1] = { ...last, ...step }
  } else {
    steps.push(step)
  }
  return steps
}

/** Whole seconds, then minutes - the scale a reader waiting on the answer actually thinks in. */
const formatSeconds = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return `${minutes}m${rest ? ` ${rest}s` : ''}`
}

/**
 * How long the step in flight has been running, counting up a second at a time.
 *
 * The wait for the model to think is the longest silence in a turn, and a number that keeps moving
 * is the difference between "it is working" and "it has hung". It restarts whenever `resetKey`
 * changes - each phrase is its own piece of work - and stays blank for the first second, so a quick
 * step does not flash a '0s' on its way past.
 */
export function StepElapsed({ resetKey, className = '' }: { resetKey: string; className?: string }) {
  const [seconds, setSeconds] = useState(0)
  useEffect(() => {
    setSeconds(0)
    const startedAt = Date.now()
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000)
    return () => clearInterval(timer)
  }, [resetKey])
  if (seconds < 1) return null
  return <span className={`tabular-nums ${className}`}>{formatSeconds(seconds)}</span>
}

/** 118400 -> "118k": these are read for their order of magnitude, not their last digit. */
const compactTokens = (tokens: number): string =>
  tokens >= 1000 ? `${(tokens / 1000).toFixed(tokens >= 10000 ? 0 : 1)}k` : `${tokens}`

/**
 * What a model step cost, under its phrase.
 *
 * The cached share is the point of showing it: it is the only way to see from the outside whether
 * prompt caching is reaching the model, and an uncached turn re-reads the whole system prompt and
 * tool catalogue on every step - most of what makes a long turn long.
 */
function StepUsage({ usage }: { usage: AssistantStep['usage'] }) {
  if (!usage?.input && !usage?.output) return null
  const cached = usage.input ? Math.round(((usage.cacheRead || 0) / usage.input) * 100) : 0
  return (
    <span className="ml-1 text-gray-400 tabular-nums">
      {!!usage.input && `${compactTokens(usage.input)} in`}
      {!!usage.input && ` (${cached}% cached)`}
      {!!usage.output && `${usage.input ? ' · ' : ''}${compactTokens(usage.output)} out`}
    </span>
  )
}

const STEP_ICON: Record<AssistantStep['kind'], typeof WrenchScrewdriverIcon> = {
  thinking: SparklesIcon,
  tool: WrenchScrewdriverIcon,
  web_search: GlobeAltIcon,
  answer: PencilSquareIcon,
  error: ExclamationTriangleIcon,
}

/**
 * One step: the phrase always, and underneath it - only when the reader asks - the arguments the
 * model passed and what came back. The detail is what turns "it said the payoff is X" into
 * something checkable, and it is the fastest way to see that the assistant read the wrong loan or
 * hit an error it then talked around.
 */
function AssistantStepRow({ step }: { step: AssistantStep }) {
  const [open, setOpen] = useState(false)
  const Icon = STEP_ICON[step.kind] || WrenchScrewdriverIcon
  const hasInput = !!step.input && Object.keys(step.input).length > 0
  const detail = hasInput || !!step.result
  const failed = step.ok === false || step.kind === 'error'
  return (
    <li className="text-xs text-gray-600">
      <div className="flex items-start gap-1.5">
        <Icon className={`mt-0.5 h-3 w-3 shrink-0 ${failed ? 'text-red-500' : 'text-green-500'}`} />
        <button
          type="button"
          disabled={!detail}
          onClick={() => setOpen((prev) => !prev)}
          className={`text-left ${detail ? 'hover:text-shade-blue' : 'cursor-default'}`}
        >
          {step.label}
          {/* A reasoning step says its own duration ("Thought for 12s"); anything else gets it here. */}
          {step.kind !== 'thinking' && step.ms !== undefined && step.ms >= 1000 ? (
            <span className="ml-1 text-gray-400">{(step.ms / 1000).toFixed(1)}s</span>
          ) : null}
          <StepUsage usage={step.usage} />
        </button>
      </div>
      {open && detail && (
        <div className="ml-4 mt-1 space-y-1">
          {hasInput && (
            <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-gray-50 p-1.5 text-[11px] text-gray-500">
              {JSON.stringify(step.input, null, 2)}
            </pre>
          )}
          {!!step.result && (
            <pre
              className={`max-h-40 overflow-auto whitespace-pre-wrap break-words rounded p-1.5 text-[11px] ${
                failed ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500'
              }`}
            >
              {step.result}
            </pre>
          )}
        </div>
      )}
    </li>
  )
}

/** The ordered list of steps, shared by a finished turn and the one in flight. */
export function AssistantStepList({ steps, className = '' }: { steps: AssistantStep[]; className?: string }) {
  if (!steps.length) return null
  return (
    <ol className={`space-y-1 border-l border-gray-200 pl-3 ${className}`}>
      {steps.map((step, index) => (
        <AssistantStepRow key={`${step.label}-${index}`} step={step} />
      ))}
    </ol>
  )
}
